// HSE Overview — sicurezza sul lavoro con scadenzario e documenti
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Shield, FileText, Award, AlertOctagon, Calendar, Users } from 'lucide-react'
import { useSendLogs } from '../../hooks/useHse'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Alert } from '../../components/ui/Alert'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { formatDate } from '../../lib/utils'
import api from '../../lib/api'

interface HSEOverview {
  expiredCertifications: number
  expiringCertifications: number
  validDocuments: number
  expiredDocuments: number
  openIncidents: number
  lastChecklist: string | null
  upcomingExpirations: Array<{
    id: string
    type: 'certification' | 'document'
    name: string
    technicianName?: string
    expiryDate: string
    status: 'expired' | 'expiring'
  }>
}

const MODULES = [
  { label: 'Lavoratori', icon: <Users className="h-5 w-5" />, href: '/hse/lavoratori', color: 'text-[var(--fc-primary)]', bg: 'bg-[color-mix(in_srgb,var(--fc-primary)_10%,transparent)]' },
  { label: 'Doc. Azienda', icon: <FileText className="h-5 w-5" />, href: '/hse/documents', color: 'text-[var(--fc-info)]', bg: 'bg-[color-mix(in_srgb,var(--fc-info)_10%,transparent)]' },
  { label: 'Abilitazioni', icon: <Award className="h-5 w-5" />, href: '/hse/certifications', color: 'text-[var(--fc-success)]', bg: 'bg-[color-mix(in_srgb,var(--fc-success)_10%,transparent)]' },
  { label: 'Infortuni', icon: <AlertOctagon className="h-5 w-5" />, href: '/hse/incidents', color: 'text-[var(--fc-danger)]', bg: 'bg-[color-mix(in_srgb,var(--fc-danger)_10%,transparent)]' },
]

export function HSEPage() {
  const navigate = useNavigate()
  const { data: logsData } = useSendLogs(10)

  const { data: overview, isLoading } = useQuery({
    queryKey: ['hse', 'overview'],
    queryFn: async () => {
      try {
        // /hse/overview non esiste — costruisce overview dai sotto-endpoint disponibili
        const [certsRes, docsRes, expiringRes] = await Promise.allSettled([
          api.get<Array<{ id: string; expiryDate?: string; issueDate?: string; type: string }>>('/hse/certifications'),
          api.get<Array<{ id: string; validTo?: string }>>('/hse/documents'),
          api.get<Array<{ id: string; type: string; technicianId?: string; expiryDate?: string }>>('/hse/certifications/expiring', { params: { days: 30 } }),
        ])

        const certs = certsRes.status === 'fulfilled' ? (certsRes.value.data ?? []) : []
        const docs = docsRes.status === 'fulfilled' ? (docsRes.value.data ?? []) : []
        const expiring = expiringRes.status === 'fulfilled' ? (expiringRes.value.data ?? []) : []

        const now = new Date()
        const expiredCerts = certs.filter((c) => c.expiryDate && new Date(c.expiryDate) < now).length
        const expiredDocs = docs.filter((d) => d.validTo && new Date(d.validTo) < now).length

        const upcomingExpirations: HSEOverview['upcomingExpirations'] = expiring.map((c) => ({
          id: c.id,
          type: 'certification' as const,
          name: c.type,
          expiryDate: c.expiryDate ?? '',
          status: c.expiryDate && new Date(c.expiryDate) < now ? 'expired' : 'expiring',
        }))

        return {
          expiredCertifications: expiredCerts,
          expiringCertifications: expiring.length,
          validDocuments: docs.filter((d) => !d.validTo || new Date(d.validTo) >= now).length,
          expiredDocuments: expiredDocs,
          openIncidents: 0,
          lastChecklist: null,
          upcomingExpirations,
        } satisfies HSEOverview
      } catch {
        return null
      }
    },
  })

  const hasAlerts = !isLoading && ((overview?.expiredCertifications ?? 0) > 0 || (overview?.expiredDocuments ?? 0) > 0)

  return (
    <div className="space-y-6">
      <PageHeader
        title="HSE"
        subtitle="Health, Safety & Environment — Sicurezza sul lavoro"
      />

      {/* Alert scadenze */}
      {hasAlerts && (
        <Alert variant="error" title="Scadenze critiche">
          {overview!.expiredCertifications > 0 && `${overview!.expiredCertifications} abilitazioni scadute. `}
          {overview!.expiredDocuments > 0 && `${overview!.expiredDocuments} documenti scaduti.`}
        </Alert>
      )}

      {/* KPI */}
      {overview && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: 'Certif. scadute', value: overview.expiredCertifications, danger: true },
            { label: 'Certif. in scadenza', value: overview.expiringCertifications, warning: true },
            { label: 'Doc. validi', value: overview.validDocuments, ok: true },
            { label: 'Incidenti aperti', value: overview.openIncidents, danger: overview.openIncidents > 0 },
          ].map(({ label, value, danger, warning, ok }) => (
            <Card key={label}>
              <p className="text-xs text-[var(--fc-foreground-muted)]">{label}</p>
              <p className={`mt-1 text-3xl font-bold ${
                danger && value > 0 ? 'text-[var(--fc-danger)]'
                : warning && value > 0 ? 'text-[var(--fc-warning)]'
                : ok ? 'text-[var(--fc-success)]'
                : 'text-[var(--fc-foreground)]'
              }`}>
                {value}
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Accesso moduli */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {MODULES.map((mod) => (
          <button
            key={mod.label}
            onClick={() => navigate(mod.href)}
            className="flex items-center gap-4 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
            aria-label={mod.label}
          >
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${mod.bg}`}>
              <span className={mod.color}>{mod.icon}</span>
            </div>
            <span className="text-base font-semibold text-[var(--fc-foreground)]">{mod.label}</span>
          </button>
        ))}
      </div>

      {/* Prossime scadenze */}
      {overview?.upcomingExpirations && overview.upcomingExpirations.length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-[var(--fc-warning)]" aria-hidden="true" />
            <h2 className="text-base font-semibold text-[var(--fc-foreground)]">Prossime scadenze</h2>
          </div>
          <ul className="divide-y divide-[var(--fc-border)]" role="list">
            {overview.upcomingExpirations.map((exp) => (
              <li key={exp.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-[var(--fc-foreground)]">{exp.name}</p>
                  {exp.technicianName && (
                    <p className="text-xs text-[var(--fc-foreground-muted)]">{exp.technicianName}</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[var(--fc-foreground-muted)]">
                    {formatDate(exp.expiryDate, 'dd/MM/yyyy')}
                  </span>
                  <Badge variant={exp.status === 'expired' ? 'danger' : 'warning'}>
                    {exp.status === 'expired' ? 'Scaduto' : 'In scadenza'}
                  </Badge>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Storico invii documenti */}
      {logsData && logsData.total > 0 && (
        <div className="rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] p-5 space-y-3">
          <h3 className="font-semibold text-[var(--fc-foreground)]">Ultimi invii documenti</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--fc-border)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--fc-foreground-muted)]">
                  <th className="pb-2 pr-4">Data</th>
                  <th className="pb-2 pr-4">Destinatario</th>
                  <th className="pb-2 pr-4">N° doc</th>
                  <th className="pb-2">Modalità</th>
                </tr>
              </thead>
              <tbody>
                {logsData.items.map((log) => (
                  <tr key={log.id} className="border-b border-[var(--fc-border)] last:border-0">
                    <td className="py-2 pr-4 text-[var(--fc-foreground-muted)]">
                      {formatDate(log.sentAt, 'dd/MM/yyyy HH:mm')}
                    </td>
                    <td className="py-2 pr-4 text-[var(--fc-foreground)]">{log.recipientEmail}</td>
                    <td className="py-2 pr-4 text-[var(--fc-foreground-muted)]">{log.documents.length}</td>
                    <td className="py-2 text-[var(--fc-foreground-muted)] capitalize">{log.deliveryMode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContextualHelp />
    </div>
  )
}
